/**
 * CSV Parser utility for user import
 * Only handles parsing - validation is done on the backend
 */
import { generateRandomPassword } from './passwordUtils';

/**
 * Column name mapping from human-readable to internal names
 */
const COLUMN_MAPPING = {
  'email': 'email',
  'nom': 'username',
  'username': 'username',
  'role': 'role',
  'rôle': 'role',
  'service desk id': 'servicedeskid',
  'servicedeskid': 'servicedeskid',
  'mode': 'mode',
  'secteurs': 'sectors',
  'sectors': 'sectors',
  'service desks creation ticket': 'allowedticketcreationdesks',
  'service desks autorisés': 'allowedticketcreationdesks',
  'service desks autorises': 'allowedticketcreationdesks',
  'service desks autorises - techniciens uniquement': 'allowedticketcreationdesks',
  'service desks autorisés - techniciens uniquement': 'allowedticketcreationdesks',
  'allowedticketcreationdesks': 'allowedticketcreationdesks',
};

/**
 * Detect the delimiter used in CSV (comma or semicolon)
 * @param {string} headerLine - First line of CSV
 * @returns {string} Detected delimiter
 */
const detectDelimiter = (headerLine) => {
  const commaCount = (headerLine.match(/,/g) || []).length;
  const semicolonCount = (headerLine.match(/;/g) || []).length;
  return semicolonCount > commaCount ? ';' : ',';
};

/**
 * Parse CSV file to array of objects
 * @param {File} file - CSV file
 * @returns {Promise<Array<Object>>} Parsed rows
 */
export const parseCSV = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (event) => {
      try {
        const text = event.target.result;
        const lines = text.split(/\r?\n/).filter(line => line.trim());

        if (lines.length < 2) {
          reject(new Error('CSV must have a header row and at least one data row'));
          return;
        }

        // Detect delimiter from header line
        const delimiter = detectDelimiter(lines[0]);

        // Parse header
        const headers = parseCSVLine(lines[0], delimiter);

        // Parse data rows
        const rows = [];
        for (let i = 1; i < lines.length; i++) {
          const values = parseCSVLine(lines[i], delimiter);
          const row = {};
          headers.forEach((header, index) => {
            // Strip parenthetical descriptions (e.g. "Role (user/technician/...)" -> "role")
            const normalizedHeader = header.trim().replace(/\s*\(.*\)\s*$/, '').toLowerCase().replace(/\s*-\s*$/, '').trim();
            const mappedHeader = COLUMN_MAPPING[normalizedHeader] || normalizedHeader;
            row[mappedHeader] = values[index]?.trim() || '';
          });
          row._lineNumber = i + 1; // For error reporting
          rows.push(row);
        }

        resolve(rows);
      } catch (error) {
        reject(error);
      }
    };

    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsText(file);
  });
};

/**
 * Parse a single CSV line, handling quoted values
 * @param {string} line
 * @param {string} delimiter - The delimiter character (comma or semicolon)
 * @returns {Array<string>}
 */
const parseCSVLine = (line, delimiter = ',') => {
  const result = [];
  let current = '';
  let inQuotes = false;

  for (let i = 0; i < line.length; i++) {
    const char = line[i];

    if (char === '"') {
      if (inQuotes && line[i + 1] === '"') {
        current += '"';
        i++; // Skip next quote
      } else {
        inQuotes = !inQuotes;
      }
    } else if (char === delimiter && !inQuotes) {
      result.push(current);
      current = '';
    } else {
      current += char;
    }
  }
  result.push(current);

  return result;
};

/**
 * Transform parsed CSV row to user object for API
 * @param {Object} row - Parsed row
 * @returns {Object} User object
 */
export const transformRowToUser = (row) => {
  const password = row.password || generateRandomPassword();
  const wasPasswordGenerated = !row.password;

  return {
    email: row.email?.trim() || '',
    username: row.username?.trim() || '',
    password,
    wasPasswordGenerated,
    role: (row.role || '').toLowerCase().trim(),
    mode: (row.mode || 'rw').toLowerCase().trim(),
    serviceDeskId: row.servicedeskid?.trim() || '',
    sectors: row.sectors?.trim() || '',
    allowedTicketCreationDesks: row.allowedticketcreationdesks?.trim() || '',
    _lineNumber: row._lineNumber
  };
};

/**
 * Generate CSV content for download (results with passwords)
 * @param {Object} results - Import results { created, updated, failed }
 * @returns {string} CSV content
 */
export const generateResultsCSV = (results) => {
  const headers = ['email', 'username', 'status', 'generatedPassword', 'error'];
  const rows = [headers.join(',')];

  results.created.forEach(item => {
    rows.push([
      item.user.email,
      item.user.username,
      'created',
      item.generatedPassword || '',
      ''
    ].map(v => `"${v}"`).join(','));
  });

  results.updated.forEach(item => {
    rows.push([
      item.user.email,
      item.user.username,
      'updated',
      '',
      ''
    ].map(v => `"${v}"`).join(','));
  });

  results.failed.forEach(item => {
    rows.push([
      item.email,
      '',
      'failed',
      '',
      item.error
    ].map(v => `"${v}"`).join(','));
  });

  return rows.join('\n');
};
